import { useEffect, useRef } from "react";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
import { googleSignInApi } from "@/portals/patient/api/patientAuthApi";
import { applyGoogleSession, consumeGoogleFlow, googleRedirectUri } from "./googleAuth";

/**
 * Google sends the browser here (/auth/google/callback) after the account
 * chooser. Existing patients land on the dashboard; new ones go to the date of
 * birth step (GoogleCompleteSignupPage).
 */
export function GoogleCallbackPage() {
  const navigate = useNavigate();
  // StrictMode runs this effect twice in dev; the code and the saved flow are
  // single use, so only the first run may spend them.
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const flow = consumeGoogleFlow(params.get("state"));

    const fail = (error: string) => navigate("/patient/auth/signin", { replace: true, state: { error } });

    if (params.get("error")) {
      // access_denied = the user closed or cancelled Google's screen.
      fail(
        params.get("error") === "access_denied"
          ? "Google sign in was cancelled."
          : "Google sign in failed. Please try again.",
      );
      return;
    }
    if (!code || !flow) {
      fail("Google sign in failed. Please try again.");
      return;
    }

    void googleSignInApi(code, googleRedirectUri(), flow.codeVerifier, flow.nonce).then(({ data, error }) => {
      if (!data) {
        fail(error ?? "Google sign in failed. Please try again.");
        return;
      }
      if (data.status === "needs_profile") {
        navigate("/patient/auth/google/complete", {
          replace: true,
          state: {
            signupToken: data.signupToken,
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
          },
        });
        return;
      }
      applyGoogleSession(data);
      navigate("/patient", { replace: true });
    });
  }, [navigate]);

  return (
    <div className="flex w-full flex-col items-center gap-3 py-10 text-sm text-[#6F7280]">
      <Icon icon="lucide:loader-circle" className="h-6 w-6 animate-spin text-[#E4002B]" />
      Signing you in with Google...
    </div>
  );
}
